
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { ProjectTemplate, Project, ProjectTask, convertTasksToJson } from "../types";
import { calculateSequentialTaskDates } from "../utils/dateUtils";

export const useProjects = (templates: ProjectTemplate[]) => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);
  const [activeTemplateForProjects, setActiveTemplateForProjects] = useState<string>("");
  
  // Dialog states
  const [isNewProjectDialogOpen, setIsNewProjectDialogOpen] = useState(false);
  const [isEditProjectDialogOpen, setIsEditProjectDialogOpen] = useState(false);
  const [isDeleteProjectDialogOpen, setIsDeleteProjectDialogOpen] = useState(false);
  const [isImportDialogOpen, setIsImportDialogOpen] = useState(false);
  const [excludeWeekends, setExcludeWeekends] = useState(true);
  
  // Project form state
  const [newProject, setNewProject] = useState({
    name: "",
    description: "",
    projectLead: "",
    startDate: "",
    endDate: "",
    type: "",
    duration: 0,
    tasks: [] as ProjectTask[]
  });

  // Load projects from Supabase
  const loadProjects = async () => {
    try {
      setLoading(true);
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        console.error('User not authenticated');
        return;
      }
      
      const { data, error } = await supabase
        .from('projects')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });
        
      if (error) {
        console.error('Error loading projects:', error);
        toast.error('Failed to load projects');
        return;
      }
      
      const mappedProjects: Project[] = data.map(project => {
        // Parse project_tasks from Json to ProjectTask[]
        let parsedTasks: ProjectTask[] = [];
        if (project.project_tasks) {
          try {
            // Handle both array and string JSON formats
            const tasksData = Array.isArray(project.project_tasks) 
              ? project.project_tasks 
              : JSON.parse(JSON.stringify(project.project_tasks));
            
            if (Array.isArray(tasksData)) {
              parsedTasks = tasksData.map((task: any) => ({
                name: task.name || '',
                duration: task.duration || 1,
                progress: task.progress || 0,
                phase: task.phase || 'Main Tasks',
                startDate: task.startDate || '',
                endDate: task.endDate || '',
                dependencies: task.dependencies || '',
                isCritical: task.isCritical || false,
                isMilestone: task.isMilestone || false,
                id: task.id,
                taskId: task.taskId
              }));
            }
          } catch (parseError) {
            console.error('Error parsing project tasks:', parseError);
          }
        }
        
        return {
          id: project.id,
          name: project.project_name,
          description: project.description || '',
          projectLead: project.project_lead || '',
          startDate: project.start_date || '',
          endDate: project.end_date || '',
          template: project.template || '',
          duration: project.duration || 0,
          excludeWeekends: project.exclude_weekends,
          lastUpdated: new Date(project.updated_at).toLocaleDateString(),
          tasks: parsedTasks
        };
      });
      
      setProjects(mappedProjects);
      
    } catch (error) {
      console.error('Error in loadProjects:', error);
      toast.error('Failed to load projects');
    } finally {
      setLoading(false);
    }
  };

  // Initialize projects on mount
  useEffect(() => {
    loadProjects();
  }, []);

  // Helper function to recalculate sequential task dates
  const recalculateTaskDatesForSave = (tasks: ProjectTask[], projectStartDate: string, excludeWeekends: boolean): ProjectTask[] => {
    if (!tasks.length || !projectStartDate) return tasks;

    const { startDates, endDates } = calculateSequentialTaskDates(
      projectStartDate,
      tasks.map(task => ({ duration: task.duration })),
      excludeWeekends
    );

    return tasks.map((task, index) => ({
      ...task,
      startDate: startDates[index] || task.startDate || '',
      endDate: endDates[index] || task.endDate || ''
    }));
  };

  const handleCreateProject = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!newProject.name.trim()) {
      toast.error('Project name is required');
      return;
    }
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        toast.error('User not authenticated');
        return;
      }

      // Recalculate task dates before saving
      const recalculatedTasks = recalculateTaskDatesForSave(
        newProject.tasks,
        newProject.startDate,
        excludeWeekends
      );

      // Calculate project end date from last task
      const projectEndDate = recalculatedTasks.length > 0 && recalculatedTasks[recalculatedTasks.length - 1].endDate
        ? recalculatedTasks[recalculatedTasks.length - 1].endDate
        : newProject.endDate;

      const { error } = await supabase
        .from('projects')
        .insert({
          user_id: user.id,
          project_name: newProject.name,
          description: newProject.description,
          project_lead: newProject.projectLead,
          start_date: newProject.startDate,
          end_date: projectEndDate,
          template: newProject.type,
          duration: recalculatedTasks.reduce((sum, task) => sum + task.duration, 0),
          exclude_weekends: excludeWeekends,
          project_tasks: convertTasksToJson(recalculatedTasks)
        });
      
      if (error) {
        console.error('Error creating project:', error);
        toast.error('Failed to create project');
        return;
      }
      
      toast.success('Project created successfully');
      setIsNewProjectDialogOpen(false);
      
      // Reset form
      setNewProject({
        name: "",
        description: "",
        projectLead: "",
        startDate: "",
        endDate: "",
        type: "",
        duration: 0,
        tasks: []
      });
      
      // Reload projects
      await loadProjects();
      
    } catch (error) {
      console.error('Error creating project:', error);
      toast.error('Failed to create project');
    }
  };

  const confirmEditProject = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedProject || !newProject.name.trim()) {
      toast.error('Project name is required');
      return;
    }
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        toast.error('User not authenticated');
        return;
      }

      // Recalculate task dates before saving
      const recalculatedTasks = recalculateTaskDatesForSave(
        newProject.tasks,
        newProject.startDate,
        excludeWeekends
      );

      // Calculate project end date from last task
      const projectEndDate = recalculatedTasks.length > 0 && recalculatedTasks[recalculatedTasks.length - 1].endDate
        ? recalculatedTasks[recalculatedTasks.length - 1].endDate
        : newProject.endDate;
      
      const { error } = await supabase
        .from('projects')
        .update({
          project_name: newProject.name,
          description: newProject.description,
          project_lead: newProject.projectLead,
          start_date: newProject.startDate,
          end_date: projectEndDate,
          duration: recalculatedTasks.reduce((sum, task) => sum + task.duration, 0),
          exclude_weekends: excludeWeekends,
          project_tasks: convertTasksToJson(recalculatedTasks)
        })
        .eq('id', selectedProject.id);
      
      if (error) {
        console.error('Error updating project:', error);
        toast.error('Failed to update project');
        return;
      }
      
      toast.success('Project updated successfully');
      setIsEditProjectDialogOpen(false);
      
      // Reload projects
      await loadProjects();
      
    } catch (error) {
      console.error('Error updating project:', error);
      toast.error('Failed to update project');
    }
  };

  const confirmDeleteProject = async () => {
    if (!selectedProject) return;
    
    try {
      const { error } = await supabase
        .from('projects')
        .delete()
        .eq('id', selectedProject.id);
      
      if (error) {
        console.error('Error deleting project:', error);
        toast.error('Failed to delete project');
        return;
      }
      
      toast.success('Project deleted successfully');
      setIsDeleteProjectDialogOpen(false);
      setSelectedProject(null);
      setSelectedProjectId(null);
      
      // Reload projects
      await loadProjects();
      
    } catch (error) {
      console.error('Error deleting project:', error);
      toast.error('Failed to delete project');
    }
  };

  const handleLoadProject = (project: Project) => {
    setSelectedProject(project);
    setSelectedProjectId(project.id);
  };

  const handleEditProject = (project: Project) => {
    setSelectedProject(project);
    setNewProject({
      name: project.name,
      description: project.description,
      projectLead: project.projectLead,
      startDate: project.startDate,
      endDate: project.endDate,
      type: project.template || "",
      duration: project.duration,
      tasks: project.tasks || []
    });
    setExcludeWeekends(project.excludeWeekends || false);
    setIsEditProjectDialogOpen(true);
  };

  const handleDeleteProject = (project: Project) => {
    setSelectedProject(project);
    setIsDeleteProjectDialogOpen(true);
  };

  const handleApplyTemplate = async (
    e: React.FormEvent,
    selectedTemplate: string,
    selectedTemplateTasks: ProjectTask[],
    templateProjectName: string,
    templateStartDate: string,
    templateEndDate: string,
    templateProjectDescription: string,
    templateProjectLead: string,
    excludeWeekends: boolean
  ): Promise<boolean> => {
    e.preventDefault();

    if (!templateProjectName.trim()) {
      toast.error('Project name is required');
      return false;
    }

    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        toast.error('User not authenticated');
        return false;
      }

      // Recalculate task dates before saving
      const recalculatedTasks = recalculateTaskDatesForSave(
        selectedTemplateTasks,
        templateStartDate,
        excludeWeekends
      );

      // Calculate project end date from last task
      const projectEndDate = recalculatedTasks.length > 0 && recalculatedTasks[recalculatedTasks.length - 1].endDate
        ? recalculatedTasks[recalculatedTasks.length - 1].endDate
        : templateEndDate;

      const { error } = await supabase
        .from('projects')
        .insert({
          user_id: user.id,
          project_name: templateProjectName,
          description: templateProjectDescription,
          project_lead: templateProjectLead,
          start_date: templateStartDate,
          end_date: projectEndDate,
          template: selectedTemplate,
          duration: recalculatedTasks.reduce((sum, task) => sum + task.duration, 0),
          exclude_weekends: excludeWeekends,
          project_tasks: convertTasksToJson(recalculatedTasks)
        });
      
      if (error) {
        console.error('Error creating project from template:', error);
        toast.error('Failed to create project from template');
        return false;
      }
      
      toast.success(`Project "${templateProjectName}" created successfully from ${selectedTemplate} template`);
      setActiveTemplateForProjects(selectedTemplate);
      
      // Reload projects
      await loadProjects();
      return true;
      
    } catch (error) {
      console.error('Error creating project from template:', error);
      toast.error('Failed to create project from template');
      return false;
    }
  };

  const handleTaskDurationChange = (index: number, increase: boolean) => {
    const updatedTasks = [...newProject.tasks];
    if (increase) {
      updatedTasks[index].duration += 1;
    } else if (updatedTasks[index].duration > 1) {
      updatedTasks[index].duration -= 1;
    }

    // Recalculate dates for all tasks after the change
    const recalculatedTasks = recalculateTaskDatesForSave(
      updatedTasks,
      newProject.startDate,
      excludeWeekends
    );

    setNewProject(prev => ({
      ...prev,
      tasks: recalculatedTasks,
      duration: recalculatedTasks.reduce((sum, task) => sum + task.duration, 0),
      endDate: recalculatedTasks.length > 0 && recalculatedTasks[recalculatedTasks.length - 1].endDate
        ? recalculatedTasks[recalculatedTasks.length - 1].endDate
        : prev.endDate
    }));
  };

  return {
    projects,
    loading,
    selectedProject,
    selectedProjectId,
    setSelectedProjectId,
    activeTemplateForProjects,
    isNewProjectDialogOpen,
    setIsNewProjectDialogOpen,
    isEditProjectDialogOpen,
    setIsEditProjectDialogOpen,
    isDeleteProjectDialogOpen,
    setIsDeleteProjectDialogOpen,
    isImportDialogOpen,
    setIsImportDialogOpen,
    excludeWeekends,
    setExcludeWeekends,
    newProject,
    setNewProject,
    handleCreateProject,
    confirmEditProject,
    confirmDeleteProject,
    handleLoadProject,
    handleEditProject,
    handleDeleteProject,
    handleApplyTemplate,
    handleTaskDurationChange,
    loadProjects
  };
};
